import styles from "./ClassWizard.module.scss";
import { PageMode } from "../../../../common/types";
import { useClassWizardPage } from "./hooks/useClassWizardPage";
import { EventWizardMainInputs } from "./components/EventWizardMainInputs";
import { RecurringEventSelector } from "../common/recurringEventSelector/RecurringEventSelector";
import { HiddenField } from "../../../../common/components/forms/inputs/hiddenField/HiddenField";
import { IClassWizardFormModel } from "./models/classWizardFormModel";
import { nameof } from "ts-simple-nameof";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { FormProvider } from "react-hook-form";
import { useState, useCallback } from "react";
import { SingleSectionPageLayout } from "../../../../common/components/common/SingleSectionPageLayout";
import { IEventWizardInitialData } from "../models/eventWizardInitialDataModel";
import { ISelectFieldModel } from "../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { useClassConflictsCheck } from "./hooks/useClassConflictsCheck";
import { EventWizardRecurringVisitorsInput } from "./components/EventWizardRecurringVisitorsInputs";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";

interface IClassWizardProps {
  pageMode: PageMode;
  hasTabs: boolean;
  initialData?: IEventWizardInitialData;
  studioInstructors?: ISelectFieldModel[];
  isLoading: boolean;
}

export const ClassWizard = ({
  pageMode,
  hasTabs,
  initialData,
  studioInstructors,
  isLoading,
}: IClassWizardProps) => {
  const {
    form,
    pageTitle,
    buttonTitle,
    dateText,
    enableMultisport,
    isIndividualEvent,
    showRecurringVisitors,
    hasRecurringVisitors,
    eventTemplates,
    onTrySave,
  } = useClassWizardPage({ pageMode, initialData, studioInstructors });

  // A class conflicts only with an existing class of the same name and start; the
  // instructor's other bookings never block it.
  const checkClassConflicts = useClassConflictsCheck(form.getValues);

  const [isRecurringActive, setIsRecurringActive] = useState(false);
  const [hasRecurringChecked, setHasRecurringChecked] = useState(false);
  const [recurringFormValid, setRecurringFormValid] = useState(true);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasRecurringConflicts, setHasRecurringConflicts] = useState(false);

  const handleRecurringStatusChange = useCallback(
    (isRecurring: boolean, hasChecked: boolean, isFormValid: boolean, hasConflicts: boolean) => {
      setIsRecurringActive(isRecurring);
      setHasRecurringChecked(hasChecked);
      setRecurringFormValid(isFormValid);
      setHasRecurringConflicts(hasConflicts);
      setHasUserInteracted(true);
    },
    [],
  );

  const isSubmitDisabled =
    !hasUserInteracted ||
    (isRecurringActive && !hasRecurringChecked) ||
    (isRecurringActive && !recurringFormValid) ||
    (isRecurringActive && hasRecurringConflicts);
  const titleComponent = (
    <>
      <div className={styles.root_title}>{pageTitle}</div>
      <div className={styles.root_date_text}>{dateText}</div>
    </>
  );

  const children = (
    <>
      <div className={styles.root_fields}>
        <EventWizardMainInputs
          pageMode={pageMode}
          initialData={initialData}
          isIndividualEvent={isIndividualEvent}
          enableMultisport={enableMultisport}
          studioInstructors={studioInstructors}
          eventTemplates={eventTemplates}
          isLoading={isLoading}
        />

        <RecurringEventSelector
          pageMode={pageMode}
          isRecurringFieldName={nameof<IClassWizardFormModel>(
            (x) => x.isRecurring,
          )}
          recurringDaysFieldName={nameof<IClassWizardFormModel>(
            (x) => x.recurringDays,
          )}
          recurringPeriodFieldName={nameof<IClassWizardFormModel>(
            (x) => x.recurringPeriod,
          )}
          recurringPeriodTypeFieldName={nameof<IClassWizardFormModel>(
            (x) => x.recurringPeriodType,
          )}
          recurringIntervalFieldName={nameof<IClassWizardFormModel>(
            (x) => x.recurringInterval,
          )}
          recurringIntervalTypeFieldName={nameof<IClassWizardFormModel>(
            (x) => x.recurringIntervalType,
          )}
          checkboxLabel="Този клас ще се повтаря"
          onRecurringStatusChange={handleRecurringStatusChange}
          onAvailabilityCheck={checkClassConflicts}
          nameFieldName={nameof<IClassWizardFormModel>((x) => x.name)}
          dateFieldName={nameof<IClassWizardFormModel>((x) => x.date)}
          durationFieldName={nameof<IClassWizardFormModel>(
            (x) => x.durationInMinutes,
          )}
        />

        <RenderWhen condition={showRecurringVisitors}>
          <EventWizardRecurringVisitorsInput
            isIndividualEvent={isIndividualEvent}
            hasRecurringVisitors={hasRecurringVisitors}
            allUsers={initialData?.allAvailableUsers}
          />
        </RenderWhen>

        <HiddenField name={nameof<IClassWizardFormModel>((x) => x.date)} />
        <HiddenField name={nameof<IClassWizardFormModel>((x) => x.studioId)} />
      </div>
      <div className={styles.root_buttons}>
        <DefaultButton
          label={buttonTitle}
          type="submit"
          tooltip={
            isRecurringActive && hasRecurringConflicts
              ? "Не може да създадете клас с конфликти в разписанието"
              : isSubmitDisabled
              ? "Първо проверете за конфликти"
              : undefined
          }
          disabled={isSubmitDisabled}
        />
      </div>
    </>
  );
  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onTrySave)}>
        <SingleSectionPageLayout
          title={titleComponent}
          children={children}
          maxHeight={hasTabs ? "86%" : "90%"}
        />
      </form>
    </FormProvider>
  );
};
